type DraftInlineStyle = 'BOLD' | 'ITALIC' | 'UNDERLINE';
type DraftBlockType = 'unstyled' | 'header-one' | 'blockquote' | string;

export interface DraftRawContent {
	blocks: DraftBlock[];
	entityMap: Record<string, DraftEntity>;
}

interface DraftBlock {
	key: string;
	text: string;
	type: DraftBlockType;
	depth: number;
	inlineStyleRanges: DraftInlineStyleRange[];
	entityRanges: DraftEntityRange[];
	data: Record<string, any>;
}

interface DraftInlineStyleRange {
	offset: number;
	length: number;
	style: DraftInlineStyle;
}

interface DraftEntityRange {
	offset: number;
	length: number;
	key: number;
}

interface DraftEntity {
	type: 'LINK';
	mutability: string;
	data: {
		url: string;
		[key: string]: any;
	};
}

export function safeJsonParse(str: string) {
    try {
        return JSON.parse(str);
    } catch {
        return str;
    }
}

// Convert Draft.js raw content to ProseMirror JSON
export function draftToProseMirrorJSON(draft: DraftRawContent): any {
	return {
		type: 'doc',
		content: draft.blocks.map((block) => {
			return {
				type: mapBlockType(block.type),
				content: convertTextWithStyles(block.text, block.inlineStyleRanges, block.entityRanges, draft.entityMap),
			};
		}),
	};
}

function convertTextWithStyles(
	text: string,
	styleRanges: DraftInlineStyleRange[],
	entityRanges: DraftEntityRange[],
	entityMap: Record<string, DraftEntity>
) {
	const markers: Array<{
		start: number;
		end: number;
		marks: any[];
	}> = [];

	// Collect inline style marks
	for (const style of styleRanges) {
		markers.push({
			start: style.offset,
			end: style.offset + style.length,
			marks: [mapInlineStyle(style.style)].filter(Boolean),
		});
	}

	// Collect link entity marks
	for (const entity of entityRanges) {
		const entityData = entityMap[entity.key];
		if (entityData?.type === 'LINK') {
			markers.push({
				start: entity.offset,
				end: entity.offset + entity.length,
				marks: [
					{
						type: 'link',
						attrs: {
							href: entityData.data.url,
						},
					},
				],
			});
		}
	}

	// Merge overlapping marks
	markers.sort((a, b) => a.start - b.start || a.end - b.end);
	const result: any[] = [];
	let pos = 0;

	for (const marker of markers) {
		if (pos < marker.start) {
			result.push({
				type: 'text',
				text: text.slice(pos, marker.start),
			});
		}

		result.push({
			type: 'text',
			text: text.slice(marker.start, marker.end),
			marks: marker.marks,
		});

		pos = marker.end;
	}

	if (pos < text.length) {
		result.push({
			type: 'text',
			text: text.slice(pos),
		});
	}

	return result;
}

function mapBlockType(type: DraftBlockType): string {
	switch (type) {
		case 'unstyled':
			return 'paragraph';
		case 'header-one':
			return 'heading';
		case 'blockquote':
			return 'blockquote';
		default:
			return 'paragraph';
	}
}

function mapInlineStyle(style: DraftInlineStyle) {
	switch (style) {
		case 'BOLD':
			return { type: 'bold' };
		case 'ITALIC':
			return { type: 'italic' };
		case 'UNDERLINE':
			return { type: 'underline' };
		default:
			return null;
	}
}
